import React, { useState, useEffect } from "react";
import Box from "@mui/joy/Box";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CropFreeIcon from "@mui/icons-material/CropFree";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Slider from "@mui/joy/Slider";
import FormControl from "@mui/joy/FormControl";
import Drawer from "@mui/joy/Drawer";
import Button from "@mui/joy/Button";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";
import MenuButton from "@mui/joy/MenuButton";
import Dropdown from "@mui/joy/Dropdown";
import Chip from "@mui/joy/Chip";
import Alert from "@mui/joy/Alert";
import Avatar from "@mui/joy/Avatar";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import QRCode from "qrcode";
import Tabs from "@mui/joy/Tabs";
import TabList from "@mui/joy/TabList";
import Tab from "@mui/joy/Tab";
import TabPanel from "@mui/joy/TabPanel";
import tabClasses from "@mui/joy/Tab/tabClasses";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { gql, useQuery, useMutation } from "@apollo/client";
import Pagination from "../components/Pagination";
import { usePagination } from "../components/usePagination";
import AssetType from "../components/AssetType";
import { getUserProfile } from "../jwt";
import {
  useActionCable,
  useChannel,
} from "@aersoftware/react-use-action-cable";
import { getWebSocketUrl } from "../utils/actionCableUrl";

// Category options with display format
const CATEGORY_OPTIONS = [
  { value: "BK", label: "BK - Motorcycles / Quadrocycles / Tricycles" },
  { value: "CR", label: "CR - Car / Station Wagon" },
  { value: "MB", label: "MB - Minibusses" },
  { value: "BU", label: "BU - Bus / Bus Train / Midibusses" },
  { value: "LD", label: "LD - LDV / Panel Van / Other light load vehicles" },
  { value: "TK", label: "TK - Truck" },
  { value: "CV", label: "CV - Caravan" },
  { value: "LT", label: "LT - Light Load Trailer" },
  { value: "HT", label: "HT - Heavy Load Trailer" },
  { value: "OV", label: "OV - Other Self Propelled" },
];

// Province options with display format
const PROVINCE_OPTIONS = [
  { value: 0, label: "GAUTENG" },
  { value: 1, label: "EASTERN_CAPE" },
  { value: 2, label: "WESTERN_CAPE" },
  { value: 3, label: "NORTHERN_CAPE" },
  { value: 4, label: "LIMPOPO" },
  { value: 5, label: "KWAZULU_NATAL" },
  { value: 6, label: "MPUMALANGA" },
  { value: 7, label: "FREE_STATE" },
  { value: 8, label: "NORTH_WEST" },
];

// Validity status options for filtering
const VALIDITY_STATUS_OPTIONS = [
  { value: "valid", label: "Valid" },
  { value: "expired", label: "Expired" },
  { value: "expiring_soon", label: "Expiring Soon" },
];

// Helper function to convert province label to integer value
const getProvinceValue = (provinceLabel: string | null | undefined): number => {
  if (!provinceLabel) return 0;

  const province = PROVINCE_OPTIONS.find(
    (option) => option.label === provinceLabel
  );

  return province ? province.value : 0;
};

// Helper function to get display label for province
const getProvinceDisplayLabel = (
  provinceLabel: string | null | undefined
): string => {
  if (!provinceLabel) return "GAUTENG";

  // Convert underscore format to display format
  return provinceLabel.replace(/_/g, " ");
};

// Helper function to get display label for category
const getCategoryDisplayLabel = (
  categoryCode: string | null | undefined
): string => {
  if (!categoryCode) return "";

  const category = CATEGORY_OPTIONS.find(
    (option) => option.value === categoryCode
  );

  return category ? category.label : categoryCode;
};

const ASSETS_QUERY = gql`
  query Assets(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $filters: AssetFilter
  ) {
    assetsConnection(
      first: $first
      after: $after
      last: $last
      before: $before
      filters: $filters
    ) {
      edges {
        node {
          id
          field01
          field02
          field03
          issueNumber
          controlNumber
          plateNumber
          regNumber
          type
          make
          model
          description
          identificationNumber
          engineNumber
          expiryDate
          category
          feeClass
          tare
          gvm
          dateOfTest
          photoUrl
          status
          province
          createdAt
          owner {
            id
            firstName
            lastName
            email
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

const UPDATE_ASSET_MUTATION = gql`
  mutation UpdateAsset(
    $id: ID!
    $category: String!
    $feeClass: String!
    $tare: Int!
    $gvm: Int!
    $dateOfTest: ISO8601Date
    $status: String!
    $province: Int!
  ) {
    updateAsset(
      input: {
        id: $id
        category: $category
        feeClass: $feeClass
        tare: $tare
        gvm: $gvm
        dateOfTest: $dateOfTest
        status: $status
        province: $province
      }
    ) {
      asset {
        id
        category
        feeClass
        tare
        gvm
        dateOfTest
        status
        province
      }
    }
  }
`;

const Row = (props: { row: any; key?: any }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(row.category || "");
  const [feeClass, setFeeClass] = useState(row.feeClass || "");
  const [tare, setTare] = useState(row.tare || "");
  const [gvm, setGvm] = useState(row.gvm || "");
  const [dateOfTest, setDateOfTest] = useState(
    row.dateOfTest ? new Date(row.dateOfTest).toISOString().split("T")[0] : ""
  );
  const [selectedProvince, setSelectedProvince] = useState(row.province || 0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [tareError, setTareError] = useState("");
  const [gvmError, setGvmError] = useState("");
  const [drawerImageScale, setDrawerImageScale] = useState(0.99);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");

  const [updateAsset] = useMutation(UPDATE_ASSET_MUTATION, {
    refetchQueries: [{ query: ASSETS_QUERY }],
  });

  const handleEdit = () => {
    setSelectedCategory(row.category || CATEGORY_OPTIONS[1].value);
    setFeeClass(row.feeClass || "");
    setTare(row.tare || "");
    setGvm(row.gvm || "");
    setDateOfTest(
      row.dateOfTest ? new Date(row.dateOfTest).toISOString().split("T")[0] : ""
    );
    setSelectedProvince(getProvinceValue(row.province));
    setErrors([]);
    setTareError("");
    setGvmError("");
    setDrawerOpen(true);
  };

  const handleQrCodeClick = async () => {
    try {
      const qrContent = `%${row.field01 || ""}%${row.field02 || ""}%${
        row.field03 || ""
      }%${row.issueNumber || ""}%${row.controlNumber || ""}%${
        row.plateNumber || ""
      }%${row.regNumber || ""}%${row.type || ""}%${row.make || ""}%${
        row.model || ""
      }%${row.description || ""}%${row.identificationNumber || ""}%${
        row.engineNumber || ""
      }%${row.expiryDate || ""}`;
      const dataUrl = await QRCode.toDataURL(qrContent, {
        width: 400,
        margin: 2,
      });
      setQrCodeDataUrl(dataUrl);
      setQrModalOpen(true);
    } catch (error) {
      console.error("Error generating QR code:", error);
    }
  };

  // Drawer zoom functions
  const handleDrawerZoomIn = () => {
    setDrawerImageScale((prev) => Math.min(2, prev + 0.1));
  };

  const handleDrawerZoomOut = () => {
    setDrawerImageScale((prev) => Math.max(1.0, prev - 0.1));
  };

  const handleDrawerZoomChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setDrawerImageScale(value / 100); // Convert percentage to scale
  };

  // Validation functions
  const validateTare = (value: string) => {
    const numValue = parseInt(value);
    if (!value) return "Tare is required";
    if (isNaN(numValue)) return "Tare must be a number";
    if (numValue <= 0) return "Tare must be greater than 0";
    return "";
  };

  const validateGvm = (value: string) => {
    const numValue = parseInt(value);
    if (!value) return "GVM is required";
    if (isNaN(numValue)) return "GVM must be a number";
    if (numValue < 0) return "GVM must be greater than or equal to 0";
    return "";
  };

  // Check if all required fields are filled and valid
  const isFormValid =
    selectedCategory &&
    feeClass &&
    tare &&
    gvm &&
    selectedProvince !== null &&
    !tareError &&
    !gvmError;

  const handleSave = async () => {
    setIsSaving(true);
    setErrors([]);

    try {
      const result = await updateAsset({
        variables: {
          id: row.id,
          category: selectedCategory,
          feeClass: feeClass,
          tare: parseInt(tare) || null,
          gvm: gvm ? parseInt(gvm) : null,
          dateOfTest: dateOfTest || null,
          status: row.status || null,
          province: selectedProvince,
        },
      });

      if (result.data?.updateAsset?.errors?.length > 0) {
        setErrors(result.data.updateAsset.errors);
      } else {
        setDrawerOpen(false);
      }
    } catch (error) {
      setErrors([error instanceof Error ? error.message : "An error occurred"]);
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      CAPTURE_MANUALLY: { color: "primary", label: "Capture Manually" },
      TO_BE_REVIEWED: { color: "warning", label: "To Be Reviewed" },
      REVIEWED: { color: "success", label: "Reviewed" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      color: "neutral",
      label: status,
    };

    return (
      <Chip
        variant="soft"
        color={config.color as any}
        size="sm"
        sx={{ fontWeight: "600" }}
      >
        {config.label}
      </Chip>
    );
  };

  return (
    <React.Fragment>
      <tr>
        <td>
          <IconButton
            aria-label="expand row"
            variant="plain"
            color="neutral"
            size="sm"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </td>
        <td>
          <Typography level="body-xs">{row.plateNumber}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.make}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.model}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.type}</Typography>
        </td>
        <td>
          <Typography level="body-xs">{row.description}</Typography>
        </td>
        <td>
          {(() => {
            const today = new Date();
            const expiryDate = new Date(row.expiryDate);
            const daysUntilExpiry = Math.ceil(
              (expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
            );

            let color = "text.primary"; // default color

            if (daysUntilExpiry < 0) {
              color = "danger.500"; // red for expired
            } else if (daysUntilExpiry <= 21) {
              color = "warning.500"; // orange for expiring soon
            } else {
              color = "success.500"; // green for valid
            }

            return (
              <Typography level="body-xs" sx={{ color }}>
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }).format(new Date(row.expiryDate))}
              </Typography>
            );
          })()}
        </td>
        <td>{getStatusChip(row.status)}</td>
        <td>
          {row.owner ? (
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Avatar
                size="sm"
                alt={`${row.owner.firstName} ${row.owner.lastName}`}
              />
              <div>
                <Typography level="body-xs">
                  {row.owner.firstName} {row.owner.lastName}
                </Typography>
                <Typography
                  level="body-xs"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "150px",
                  }}
                >
                  {row.owner.email}
                </Typography>
              </div>
            </Box>
          ) : (
            <Typography level="body-xs" color="neutral" />
          )}
        </td>
        <td>
          {row.status !== "REVIEWED" && (
            <Dropdown>
              <MenuButton
                slots={{ root: IconButton }}
                slotProps={{
                  root: {
                    variant: "plain",
                    color: "neutral",
                    size: "sm",
                    sx: {
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: "background.level1",
                      },
                    },
                  },
                }}
              >
                <MoreHorizIcon fontSize="small" />
              </MenuButton>
              <Menu size="sm" sx={{ minWidth: 140 }}>
                <MenuItem onClick={handleEdit}>Review</MenuItem>
              </Menu>
            </Dropdown>
          )}
        </td>
      </tr>
      {open && (
        <>
          <tr>
            <td colSpan={8}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", gap: 2, flex: 1 }}>
                  <Table size="sm" sx={{ flex: 1 }}>
                    <tbody>
                      <tr>
                        <td>
                          <Typography level="body-xs">QR CODE</Typography>
                        </td>
                        <td>
                          <Typography
                            level="body-xs"
                            sx={{
                              color: "text.primary",
                              cursor: "pointer",
                              fontWeight: "bold",
                              textDecoration: "none",
                              "&:hover": {
                                color: "primary.500",
                              },
                            }}
                            onClick={handleQrCodeClick}
                          >
                            {row.regNumber} QR CODE
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">
                            Date added to system
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.createdAt
                              ? new Intl.DateTimeFormat("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }).format(new Date(row.createdAt))
                              : ""}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Field 01</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.field01}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Field 02</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.field02}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Field 03</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.field03}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Issue Number</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.issueNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">
                            Control Number
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.controlNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Plate Number</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.plateNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Reg Number</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.regNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Asset Type</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.type}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Make</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.make}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Model</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.model}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Description</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.description}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">
                            Identification Number
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.identificationNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Engine Number</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.engineNumber}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Expiry Date</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {new Intl.DateTimeFormat("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }).format(new Date(row.expiryDate))}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Category</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {getCategoryDisplayLabel(row.category)}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Fees Class</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.feeClass}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">GVM</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.gvm}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Tare</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">{row.tare}</Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Province</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.province
                              ? getProvinceDisplayLabel(row.province)
                              : ""}
                          </Typography>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <Typography level="body-xs">Date of Test</Typography>
                        </td>
                        <td>
                          <Typography level="body-xs">
                            {row.dateOfTest
                              ? new Intl.DateTimeFormat("en-US", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }).format(new Date(row.dateOfTest))
                              : row.status === "REVIEWED"
                              ? "NO TEST DATE"
                              : ""}
                          </Typography>
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {row.photoUrl && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: 1,
                        minHeight: 400,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "480px",
                          maxHeight: "640px",
                          width: "100%",
                          borderRadius: 8,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={row.photoUrl}
                          alt="Asset Photo"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </td>
          </tr>
        </>
      )}
      <Drawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        anchor="right"
        size="md"
        sx={{
          "& .MuiDrawer-paper": {
            width: "600px",
            p: 0,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.surface",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.level1",
            }}
          >
            <Typography level="h4" sx={{ mb: 1, fontWeight: "bold" }}>
              Edit Asset
            </Typography>
            <Typography level="body-sm" color="neutral">
              {row.plateNumber} • {row.make} {row.model}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {/* Error Messages */}
            {errors.length > 0 && (
              <Alert color="danger" sx={{ mb: 3 }}>
                <Typography level="body-sm">
                  {errors.map((error, index) => (
                    <div key={index}>{error}</div>
                  ))}
                </Typography>
              </Alert>
            )}

            {/* Asset Image */}
            {row.photoUrl && (
              <Box sx={{ mb: 4 }}>
                <Typography
                  level="body-sm"
                  sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
                >
                  Asset Image
                </Typography>
                <Box
                  sx={{
                    position: "relative",
                    width: "480px",
                    height: "640px",
                    borderRadius: 3,
                    overflow: "auto",
                    border: "2px solid",
                    borderColor: "divider",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    display: "flex",
                    alignItems: drawerImageScale <= 1 ? "center" : "flex-start",
                    justifyContent:
                      drawerImageScale <= 1 ? "center" : "flex-start",
                  }}
                >
                  <img
                    src={row.photoUrl}
                    alt="Asset Photo"
                    style={{
                      width: `${480 * drawerImageScale}px`,
                      height: `${640 * drawerImageScale}px`,
                      objectFit: "contain",
                      transition: "transform 0.2s ease-out",
                      display: "block",
                    }}
                  />
                </Box>

                {/* Zoom Controls */}
                <Box
                  sx={{
                    mt: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    maxWidth: "400px",
                  }}
                >
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={handleDrawerZoomOut}
                    disabled={drawerImageScale <= 1.0}
                    sx={{
                      "&:hover": {
                        backgroundColor: "background.level2",
                      },
                    }}
                  >
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Slider
                      value={drawerImageScale * 100}
                      onChange={handleDrawerZoomChange}
                      min={99}
                      max={200}
                      step={5}
                      sx={{
                        flex: 1,
                        "--Slider-trackSize": "6px",
                        "--Slider-thumbSize": "16px",
                        "--Slider-thumbBackground":
                          "var(--joy-palette-primary-500)",
                        "--Slider-trackBackground":
                          "var(--joy-palette-neutral-200)",
                        "--Slider-trackFillBackground":
                          "var(--joy-palette-primary-500)",
                      }}
                    />
                  </Box>

                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={handleDrawerZoomIn}
                    disabled={drawerImageScale >= 2}
                    sx={{
                      "&:hover": {
                        backgroundColor: "background.level2",
                      },
                    }}
                  >
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Category Dropdown */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Category *
              </Typography>
              <Select
                value={selectedCategory}
                open={selectOpen}
                onChange={(event, newValue) => {
                  setSelectedCategory(newValue || "");
                  setSelectOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            {/* Fees Class */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Fees Class *
              </Typography>
              <Input
                value={feeClass}
                onChange={(e) => setFeeClass(e.target.value)}
                placeholder="Enter fees class"
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
            </FormControl>

            {/* GVM */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                GVM *
              </Typography>
              <Input
                type="number"
                value={gvm}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow 0 and positive integers
                  if (value === "" || /^[0-9]\d*$/.test(value)) {
                    setGvm(value);
                    setGvmError(validateGvm(value));
                  }
                }}
                placeholder="Enter GVM"
                color={gvmError ? "danger" : "neutral"}
                min={0}
                required
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
              {gvmError && (
                <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                  {gvmError}
                </Typography>
              )}
            </FormControl>

            {/* Tare */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Tare *
              </Typography>
              <Input
                type="number"
                value={tare}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow positive integers
                  if (value === "" || /^[1-9]\d*$/.test(value)) {
                    setTare(value);
                    setTareError(validateTare(value));
                  }
                }}
                placeholder="Enter tare"
                color={tareError ? "danger" : "neutral"}
                min={1}
                required
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
              {tareError && (
                <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                  {tareError}
                </Typography>
              )}
            </FormControl>

            {/* Date of Test */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Date of Test
              </Typography>
              <Input
                type="date"
                value={dateOfTest}
                onChange={(e) => {
                  const value = e.target.value;
                  setDateOfTest(value);
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiInput-input": {
                    py: 1.5,
                  },
                }}
              />
            </FormControl>

            {/* Province Dropdown */}
            <FormControl sx={{ mb: 4, width: "100%" }}>
              <Typography
                level="body-sm"
                sx={{ mb: 2, fontWeight: "600", color: "text.primary" }}
              >
                Province *
              </Typography>
              <Select
                value={selectedProvince}
                onChange={(event, newValue) => {
                  setSelectedProvince(newValue || 0);
                }}
                sx={{
                  borderRadius: 2,
                  "& .MuiSelect-select": {
                    py: 1.5,
                  },
                }}
              >
                {PROVINCE_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {getProvinceDisplayLabel(option.label)}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 3,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.level1",
            }}
          >
            <Button
              variant="outlined"
              color="neutral"
              onClick={() => setDrawerOpen(false)}
              disabled={isSaving}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1.5,
                fontWeight: "600",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSave}
              disabled={isSaving || !isFormValid}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1.5,
                fontWeight: "600",
              }}
            >
              {isSaving ? "Saving..." : "Mark as Reviewed"}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* QR Code Modal */}
      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            backgroundColor: "background.surface",
            borderRadius: 3,
            p: 4,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <Typography level="h4" sx={{ mb: 3, fontWeight: "bold" }}>
            QR Code - {row.regNumber}
          </Typography>
          {qrCodeDataUrl && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={qrCodeDataUrl}
                alt="QR Code"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                }}
              />
            </Box>
          )}
        </Box>
      </Modal>
    </React.Fragment>
  );
};

const AssetsTable = ({
  plateNumber,
  make,
  model,
  type,
  regNumber,
  identificationNumber,
  validityStatus,
}: {
  plateNumber: string;
  make: string;
  model: string;
  type: string;
  regNumber: string;
  identificationNumber: string;
  validityStatus: string;
}) => {
  // Helper function to get validity status chip
  const getValidityStatusChip = (status: string) => {
    if (!status) return null;

    const statusConfig = {
      valid: { color: "success", label: "Valid" },
      expired: { color: "danger", label: "Expired" },
      expiring_soon: { color: "warning", label: "Expiring Soon" },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    if (!config) return null;

    return (
      <Chip
        variant="soft"
        color={config.color as any}
        size="sm"
        sx={{ ml: 1, fontWeight: "600" }}
      >
        {config.label}
      </Chip>
    );
  };
  const [pageSize] = useState(10);
  const { data, loading, error, fetchMore, refetch } = useQuery(ASSETS_QUERY, {
    variables: {
      first: pageSize,
      filters: {
        plateNumber: plateNumber || undefined,
        make: make || undefined,
        model: model || undefined,
        type: type || undefined,
        regNumber: regNumber || undefined,
        identificationNumber: identificationNumber || undefined,
        validityStatus: validityStatus || undefined,
      },
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext,
    canGoPrevious,
  } = usePagination({
    pageSize,
    data,
    fetchMore,
    getPageInfo: (data) => data.assetsConnection.pageInfo,
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) return prev;
      return {
        assetsConnection: {
          ...fetchMoreResult.assetsConnection,
          edges: [...fetchMoreResult.assetsConnection.edges],
        },
      };
    },
  });

  // Setup WebSocket connection
  const { actionCable } = useActionCable(getWebSocketUrl());
  const channel = useChannel(actionCable);

  useEffect(() => {
    channel.subscribe(
      { channel: "ApplicationChannel" },
      {
        received: (wsData) => {
          if (wsData.type === "asset_updated") {
            refetch();
          }
        },
      }
    );

    return () => {
      channel.unsubscribe();
    };
  }, [refetch]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <Typography level="body-lg" color="danger">
          Error loading data: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "3%", padding: "12px 6px" }}></th>
              <th style={{ width: "8%", padding: "12px 6px" }}>Plate Number</th>
              <th style={{ width: "12%", padding: "12px 6px" }}>Make</th>
              <th style={{ width: "12%", padding: "12px 6px" }}>Model</th>
              <th style={{ width: "15%", padding: "12px 6px" }}>Type</th>
              <th style={{ width: "10%", padding: "12px 6px" }}>Description</th>
              <th style={{ width: "8%", padding: "12px 6px" }}>Expiry Date</th>
              <th style={{ width: "8%", padding: "12px 6px" }}>Status</th>
              <th style={{ width: "12%", padding: "12px 6px" }}>Owner</th>
              <th style={{ width: "5%", padding: "12px 6px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading || isLoadingMore ? (
              <tr>
                <td colSpan={10}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    <CircularProgress size="sm" />
                  </Box>
                </td>
              </tr>
            ) : data?.assetsConnection?.edges?.length === 0 ? (
              <tr>
                <td colSpan={10}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                    }}
                  >
                    <Typography level="body-lg" color="neutral">
                      {plateNumber ||
                      make ||
                      model ||
                      type ||
                      regNumber ||
                      identificationNumber ||
                      validityStatus
                        ? "No assets found matching your filters."
                        : "No assets found."}
                    </Typography>
                  </Box>
                </td>
              </tr>
            ) : (
              data?.assetsConnection?.edges?.map(({ node: row }) => (
                <Row key={row.id} row={row} />
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      <Pagination
        pageInfo={pageInfo}
        isLoadingMore={isLoadingMore}
        onPageChange={handlePageChange}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
      />
    </React.Fragment>
  );
};

// Component for the current assets list functionality
const AssetsList = () => {
  const [plateNumber, setPlateNumber] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [type, setType] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [validityStatus, setValidityStatus] = useState("");

  const [debouncedPlateNumber, setDebouncedPlateNumber] = useState("");
  const [debouncedMake, setDebouncedMake] = useState("");
  const [debouncedModel, setDebouncedModel] = useState("");
  const [debouncedType, setDebouncedType] = useState("");
  const [debouncedRegNumber, setDebouncedRegNumber] = useState("");
  const [debouncedIdentificationNumber, setDebouncedIdentificationNumber] =
    useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPlateNumber(plateNumber);
    }, 300);
    return () => clearTimeout(timer);
  }, [plateNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedMake(make);
    }, 300);
    return () => clearTimeout(timer);
  }, [make]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedModel(model);
    }, 300);
    return () => clearTimeout(timer);
  }, [model]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedType(type);
    }, 300);
    return () => clearTimeout(timer);
  }, [type]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRegNumber(regNumber);
    }, 300);
    return () => clearTimeout(timer);
  }, [regNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedIdentificationNumber(identificationNumber);
    }, 300);
    return () => clearTimeout(timer);
  }, [identificationNumber]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      ></Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 3,
          width: "100%",
        }}
      >
        {/* First Row - 4 filters */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Plate Number
            </Typography>
            <Input
              placeholder="Search by plate number..."
              startDecorator={<SearchIcon />}
              endDecorator={
                plateNumber && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setPlateNumber("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={plateNumber}
              onChange={(event) => setPlateNumber(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Make
            </Typography>
            <Input
              placeholder="Search by make..."
              startDecorator={<SearchIcon />}
              endDecorator={
                make && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setMake("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={make}
              onChange={(event) => setMake(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Model
            </Typography>
            <Input
              placeholder="Search by model..."
              startDecorator={<SearchIcon />}
              endDecorator={
                model && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setModel("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={model}
              onChange={(event) => setModel(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Type
            </Typography>
            <Input
              placeholder="Search by type..."
              startDecorator={<SearchIcon />}
              endDecorator={
                type && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setType("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={type}
              onChange={(event) => setType(event.target.value)}
            />
          </FormControl>
        </Box>

        {/* Second Row - 4 filters */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Reg Number
            </Typography>
            <Input
              placeholder="Search by reg number..."
              startDecorator={<SearchIcon />}
              endDecorator={
                regNumber && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setRegNumber("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={regNumber}
              onChange={(event) => setRegNumber(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              VIN
            </Typography>
            <Input
              placeholder="Search by VIN..."
              startDecorator={<SearchIcon />}
              endDecorator={
                identificationNumber && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => setIdentificationNumber("")}
                  >
                    <ClearIcon />
                  </IconButton>
                )
              }
              value={identificationNumber}
              onChange={(event) => setIdentificationNumber(event.target.value)}
            />
          </FormControl>

          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <Typography level="body-sm" sx={{ mb: 0.5 }}>
              Validity Status
            </Typography>
            <Select
              placeholder="All Assets"
              value={validityStatus}
              onChange={(_, newValue) => setValidityStatus(newValue || "")}
              sx={{
                borderRadius: "6px",
              }}
            >
              <Option value="">All Assets</Option>
              {VALIDITY_STATUS_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <AssetsTable
        plateNumber={debouncedPlateNumber}
        make={debouncedMake}
        model={debouncedModel}
        type={debouncedType}
        regNumber={debouncedRegNumber}
        identificationNumber={debouncedIdentificationNumber}
        validityStatus={validityStatus}
      />
    </>
  );
};

// Main Assets component with conditional UI
const Assets = () => {
  const currentUserProfile = getUserProfile();
  const isSuperUser = currentUserProfile?.currentRole === "SUPER_USER";

  // For non-super users, show the old UI without tabs
  if (!isSuperUser) {
    return (
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: "column",
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Assets
        </Typography>
        <AssetsList />
      </Box>
    );
  }

  // For super users, show the tabbed interface
  return (
    <Box
      sx={{
        display: "flex",
        mb: 1,
        gap: 1,
        flexDirection: "column",
        flexWrap: "wrap",
        justifyContent: "space-between",
      }}
    >
      <Typography level="h2" component="h1">
        Assets
      </Typography>
      <Tabs defaultValue={0} sx={{ bgcolor: "transparent" }}>
        <TabList
          tabFlex={1}
          size="sm"
          sx={{
            pl: { xs: 0, md: 4 },
            justifyContent: "left",
            [`&& .${tabClasses.root}`]: {
              fontWeight: "600",
              flex: "initial",
              color: "text.tertiary",
              [`&.${tabClasses.selected}`]: {
                bgcolor: "transparent",
                color: "text.primary",
                "&::after": {
                  height: "2px",
                  bgcolor: "primary.500",
                },
              },
            },
          }}
        >
          <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={0}>
            Asset List
          </Tab>
          <Tab sx={{ borderRadius: "6px 6px 0 0" }} indicatorInset value={1}>
            Asset Types
          </Tab>
        </TabList>

        <TabPanel value={0}>
          <AssetsList />
        </TabPanel>
        <TabPanel value={1}>
          <AssetType />
        </TabPanel>
      </Tabs>
    </Box>
  );
};

export default Assets;
